import React, { useState, useEffect } from "react";
import { BiDollar, BiEuro } from "react-icons/bi";
import { IoPencil, IoReload } from "react-icons/io5";
import { TbChevronsUp, TbDownload, TbLogs, TbPlus } from "react-icons/tb";
import SingleLoaderItem from "../../Loaders/SingleLoaderItem";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { HiOutlineWallet } from "react-icons/hi2";
import AllCardsModal from "./AllCardsModal";
import FormatDateTime from "../../Functions/FormatDate";
import MandatorySign from "../../Components/MandatorySign";
import { MdStar } from "react-icons/md";
import NewCardChargeModal from "./NewCardChargeModal";
import NewCardModal from "./NewCardModal";
import Spinner from "../../Components/Spinner";
import { useLanguage } from "../../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;
const STRIPE_PUBLISHABLE_KEY_TEST = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY_TEST);

const useCountUp = (target, duration = 1500) => {
    const [value, setValue] = useState(0);

    useEffect(() => {
        let start;
        let animationFrame;

        const step = (timestamp) => {
            if (!start) start = timestamp;

            const progress = Math.min((timestamp - start) / duration, 1);

            const current = progress * target;

            setValue(current);

            if (progress < 1) {
                animationFrame = requestAnimationFrame(step);
            }
        };

        animationFrame = requestAnimationFrame(step);

        return () => cancelAnimationFrame(animationFrame);
    }, [target]);

    return value;
};

const formatNumber = (num) => {
    return num.toFixed(2);
};

const Wheel = ({ digit, offset }) => {
    const digits = [...Array(50).keys()].map((n) => n % 10);

    return (
        <div className="wheel">
            <div
                className="wheel_inner"
                style={{
                    transform: `translateY(-${(digit + offset) * 28}px)`
                }}
            >
                {digits.map((n, i) => (
                    <div key={i} className="wheel_item">
                        {n}
                    </div>
                ))}
            </div>
        </div>
    );
};

const PaymentMethod = ({ account_id, fetchUserData }) => {
    const {t} = useLanguage()
    const [paymentData, setPaymentData] = useState([])
    const [loadingPaymentData, setLoadingPaymentData] = useState(false)
    const [balance, setBalance] = useState(0)
    const animated = useCountUp(balance || 0, 1000);
    const [cards, setCards] = useState([])
    const [charges, setCharges] = useState([])
    const [showNewCardChargeModal, setShowNewCardChargeModal] = useState(false)
    const [showNewCardModal, setShowNewCardModal] = useState(false)
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [openAllCardsModal, setOpenAllCardsModal] = useState(false)
    const [defaultCard, setDefaultCard] = useState(null)
    const [loadingDownload, setLoadingDownload] = useState(false)

    const formatted = formatNumber(animated);
    const chars = formatted.split("");

    const getPaymentMetadata = async () => {
        setLoadingPaymentData(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_payment_metadata`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            setCards(data.cards)
            setCharges(data.charges)
            const defaultCard = data.cards.find(card => card.is_default);
            setDefaultCard(defaultCard || null);
            setBalance(data.wallet_balance_nano);
        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingPaymentData(false);
        }
    };

    const downloadCSV = () => {
        setLoadingDownload(true)

        if (!charges || charges.length === 0) {
            setLoadingDownload(false)
            return
        };

        setTimeout(() => {
            const headers = ["Status", "Amount ($)", "Date", "Failure reason"];

            const rows = charges.map((t) => [
                t.status,
                t.amount_nano,
                new Date(t.created_at).toLocaleString(),
                t.failure_reason
            ]);

            const csvContent = [
                headers.join(","),
                ...rows.map((row) => row.map(value => `"${value}"`).join(",")),
            ].join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "charges.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setLoadingDownload(false)
        }, 2000);

    };

    useEffect(() => {
        getPaymentMetadata()
    }, [])

    return (
        <div className="payment_method_cont">
            <div className="payment_method_cont_inner">
                <div className="card_main_cont">
                    <div className="card_cont fade-in">
                        {loadingPaymentData ? (
                            <div className="card_loader">
                                <div className="card_loader_header fade-in"></div>
                                <div className="card_loader_center">
                                    <div className="card_loader_chip fade-in"></div>
                                    <div className="card_loader_date fade-in"></div>
                                </div>
                                <div className="card_loader_footer fade-in"></div>
                            </div>
                        ) : (
                            cards.length > 0 ? (() => {
                                const defaultCard = cards.find(card => card.is_default);
                                const card = defaultCard || cards[0];

                                return (
                                    <div className="card_cont_inner">
                                        <div className="card_cont_header">
                                            <p className="fade-in">
                                                {card.payment_method_name || "Card"}
                                            </p>
                                            <i>
                                                <MdStar />
                                            </i>
                                        </div>

                                        <div className="card_cont_chip_cont fade-in">
                                            <div className="card_cont_chip"></div>
                                            <p>
                                                {String(card.exp_month).padStart(2, "0")}/
                                                {String(card.exp_year).slice(-2).padStart(2, "0")}
                                            </p>
                                        </div>

                                        <div className="card_cont_footer">
                                            <p className="fade-in">
                                                xxxx xxxx xxxx {card.last4}
                                            </p>
                                            <p className="fade-in">{card.brand?.toUpperCase() || "Card"}</p>
                                        </div>
                                    </div>
                                );
                            })() : (
                                <div className="no_card_item">
                                    <button
                                        className="header_wallet_btn"
                                        onClick={() => setShowNewCardChargeModal(true)}
                                    >
                                        <i><TbPlus /></i> {t.billing.add_card}
                                    </button>
                                </div>
                            )
                        )}

                    </div>
                    {cards.length > 0 && (
                        loadingPaymentData ? (
                            <button className="see_cards_btn see_cards_btn_loading fade-in"></button>
                        ) : (

                            <button className="see_cards_btn fade-in" onClick={() => setOpenAllCardsModal(true)}>
                                <span className="fade-in"><HiOutlineWallet /> {t.billing.open_wallet}</span>
                            </button>
                        )
                    )}
                </div>
                <div className="wallet_cont">
                    <div className="header_wallet fade-in">
                        {loadingPaymentData ? (
                            <div className="loading_cypher_header">
                                <div className="loading_cypher fade-in"></div>
                                <div className="loading_cypher fade-in"></div>
                            </div>
                        ) : (
                            <div className="credit_number_cont">
                                <p className="credit_number_cont_title">{t.billing.credit_balance}</p>
                                <div className="credit_number_cont_inner">
                                    <p className="credit_number fade-in">
                                        <i><BiDollar /></i>

                                        <span className="number_group">
                                            {chars.map((c, i) => {
                                                if (c === ".") {
                                                    return (
                                                        <span key={i} className="dot">
                                                            .
                                                        </span>
                                                    );
                                                }

                                                return (
                                                    <Wheel
                                                        key={i}
                                                        digit={Number(c)}
                                                        offset={10}
                                                    />
                                                );
                                            })}
                                        </span>
                                    </p>
                                    {cards.length > 0 ? (
                                        <button className="header_wallet_btn fade-in" onClick={() => setShowNewCardChargeModal(true)}>
                                            <TbChevronsUp />
                                            {t.billing.top_up}
                                        </button>
                                    ) : (
                                        <button className="header_wallet_btn_non_active fade-in">
                                            <TbChevronsUp />
                                            {t.billing.top_up}
                                        </button>
                                    )}
                                </div>

                            </div>
                        )}

                    </div>

                    <div className="wallet_body fade-in">
                        <div className="wallet_body_header fade-in">
                            <p>
                                <TbLogs />
                                {t.billing.payment_activity}
                            </p>
                            <div className="editor_terminal_header_btn_cont">
                                {charges.length > 0 && !loadingPaymentData && (
                                    <button className="main_mcp_cont_chat_btn fade-in" onClick={downloadCSV}>
                                        {loadingDownload ? (
                                            <Spinner loading={true} />
                                        ) : (
                                            <TbDownload />
                                        )}
                                    </button>
                                )}
                                <button className="main_mcp_cont_chat_btn fade-in" onClick={getPaymentMetadata}>
                                    {loadingPaymentData ? (
                                        <Spinner loading={true} />
                                    ) : (
                                        <IoReload />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="wallet_body_body">
                            {loadingPaymentData ? (
                                <SingleLoaderItem length={4} />
                            ) : (
                                charges.length > 0 ? (
                                    charges.map((charges) => (
                                        <div className="wallet_body_body_item fade-in">
                                            <p>
                                                <i>
                                                    {charges.currency == "usd" && (
                                                        <BiDollar />
                                                    )}
                                                    {charges.currency == "eur" && (
                                                        <BiEuro />
                                                    )}
                                                </i>
                                                {(charges.amount_nano).toFixed(2)}{" "}
                                                {charges.description && `- ${charges.description}`}
                                            </p>
                                            <div className="wallet_body_body_item_end">
                                                <p>
                                                    {FormatDateTime(charges.created_at)}
                                                </p>
                                                {charges.status != "succeeded" && (
                                                    <div className="charge_status">
                                                        <MandatorySign message={charges.failure_reason || "An error occured during the payment... retry!"} icon="info" size="14px" />
                                                    </div>
                                                )}
                                                {charges.status == "succeeded" && (
                                                    <span className="status_dot active" title="Uploaded correctly"></span>
                                                )}
                                            </div>
                                        </div>

                                    ))
                                ) : (
                                    <div className="empty_chart">
                                        <p>{t.no_data}</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Elements stripe={stripePromise}>
                <NewCardChargeModal
                    account_id={account_id}
                    show={showNewCardChargeModal}
                    handleClose={() => setShowNewCardChargeModal(false)}
                    defaultCard={defaultCard}
                    fetchData={getPaymentMetadata}
                    fetchUserData={fetchUserData}
                />
            </Elements>

            <Elements stripe={stripePromise}>
                <NewCardModal
                    account_id={account_id}
                    show={showNewCardModal}
                    handleClose={() => setShowNewCardModal(false)}
                    defaultCard={defaultCard}
                    fetchData={getPaymentMetadata}
                />
            </Elements>

            <AllCardsModal
                account_id={account_id}
                show={openAllCardsModal}
                handleClose={() => setOpenAllCardsModal(false)}
                cards={cards}
                setShowNewCardModal={setShowNewCardModal}
                loadingPaymentData={loadingPaymentData}
                fetchData={getPaymentMetadata}
            />


        </div >
    );
};

export default PaymentMethod;